import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  FlexCode,
  FlexCodeTransactionStatus,
  FlexCostCode,
  FlexCostType,
  FlexGlCode,
  FlexVendor,
  HighnoteTransactionEvent,
  HighnoteTransactionStatus,
} from 'API';
import {
  updateFlexCodeTransactionStatus,
  updateTransactionFlexCode,
  updateTransactionFlexCostCode,
  updateTransactionFlexCostType,
  updateTransactionFlexGlCode,
  updateTransactionFlexVendor,
  applyLocalTransactionUpdates,
} from 'app/pages/store/transactionsSlice';
import {
  searchFlexCodes,
  searchFlexCostCodes,
  searchFlexCostTypes,
  searchFlexVendors,
} from 'app/pages/store/flexCodingSlice';
import {
  useFlexStructuredMode,
  GL_CODE_CLEAR_CHANGES,
  PROJECT_CLEAR_CHANGES,
} from 'app/hooks/useFlexStructuredMode';

export type FlexClientProps = {
  transaction?: HighnoteTransactionEvent;
};

export const useFlexClientUtils = ({ transaction }: FlexClientProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isStructuredMode } = useFlexStructuredMode();

  // Keep a ref to the latest transaction so click handlers always read fresh
  // values, regardless of whether dropdown components captured stale callback
  // closures (see SEE-879 — the inline GL/project edits route through nested
  // dropdown components that can hold a stale onValueChange reference).
  const transactionRef = useRef(transaction);
  transactionRef.current = transaction;

  const isFlexReviewed = transaction?.flexCodeTransactionStatus === FlexCodeTransactionStatus.REVIEWED;
  const isFlexApproved = transaction?.flexCodeTransactionStatus === FlexCodeTransactionStatus.APPROVED;
  const isTransactionCleared = transaction?.status !== HighnoteTransactionStatus.APPROVED;
  const flexCodeTransactionNeedsReview =
    transaction?.flexCodeTransactionStatus === null ||
    transaction?.flexCodeTransactionStatus === FlexCodeTransactionStatus.NEEDS_REVIEW;
  const flexCodeTransactionReviewed =
    transaction?.flexCodeTransactionStatus === FlexCodeTransactionStatus.REVIEWED;

  const { id } = transaction || {};
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);

  const handleUpdateFlexCostCode = useCallback(
    (option: FlexCostCode | null) => {
      if (!id) return;
      dispatch(
        applyLocalTransactionUpdates([
          { id, changes: { flexCostCode: option, flexCostCodeId: option?.id ?? null } },
        ])
      );
      dispatch(
        updateTransactionFlexCostCode({ paidolId: selectedPaidolId, id, flexCostCodeId: option?.id ?? null })
      );
    },
    [dispatch, selectedPaidolId, id]
  );

  const handleUpdateFlexCode = useCallback(
    (option: FlexCode | null) => {
      if (!id) return;
      dispatch(
        applyLocalTransactionUpdates([{ id, changes: { flexCode: option, flexCodeId: option?.id ?? null } }])
      );
      dispatch(updateTransactionFlexCode({ paidolId: selectedPaidolId, id, flexCodeId: option?.id ?? null }));

      if (isStructuredMode) {
        dispatch(applyLocalTransactionUpdates([{ id, changes: PROJECT_CLEAR_CHANGES }]));
        dispatch(updateTransactionFlexCostCode({ paidolId: selectedPaidolId, id, flexCostCodeId: null }));
        dispatch(updateTransactionFlexCostType({ paidolId: selectedPaidolId, id, flexCostTypeId: null }));
        dispatch(updateTransactionFlexVendor({ paidolId: selectedPaidolId, id, flexVendorId: null }));
      }
    },
    [dispatch, selectedPaidolId, id, isStructuredMode]
  );

  const handleUpdateFlexCostType = useCallback(
    (option: FlexCostType | null) => {
      if (!id) return;
      dispatch(
        applyLocalTransactionUpdates([
          { id, changes: { flexCostType: option, flexCostTypeId: option?.id ?? null } },
        ])
      );
      dispatch(
        updateTransactionFlexCostType({ paidolId: selectedPaidolId, id, flexCostTypeId: option?.id ?? null })
      );
    },
    [dispatch, selectedPaidolId, id]
  );

  const handleUpdateFlexVendor = useCallback(
    (option: FlexVendor | null) => {
      if (!id) return;
      dispatch(
        applyLocalTransactionUpdates([
          { id, changes: { flexVendor: option, flexVendorId: option?.id ?? null } },
        ])
      );
      dispatch(
        updateTransactionFlexVendor({ paidolId: selectedPaidolId, id, flexVendorId: option?.id ?? null })
      );
    },
    [dispatch, selectedPaidolId, id]
  );

  const handleUpdateFlexGlCode = useCallback(
    (option: FlexGlCode | null) => {
      if (!id) return;

      // Optimistic local update
      dispatch(
        applyLocalTransactionUpdates([
          { id, changes: { flexGlCode: option, flexGlCodeId: option?.id ?? null } },
        ])
      );
      dispatch(
        updateTransactionFlexGlCode({ paidolId: selectedPaidolId, id, flexGlCodeId: option?.id ?? null })
      );

      // Read latest transaction state from ref to avoid stale closures
      // captured by memoized dropdown wrappers.
      const currentTransaction = transactionRef.current;

      if (isStructuredMode) {
        if (!option) {
          // GL cleared — clear all dependents
          dispatch(applyLocalTransactionUpdates([{ id, changes: GL_CODE_CLEAR_CHANGES }]));
          dispatch(updateTransactionFlexCode({ paidolId: selectedPaidolId, id, flexCodeId: null }));
          dispatch(updateTransactionFlexCostCode({ paidolId: selectedPaidolId, id, flexCostCodeId: null }));
          dispatch(updateTransactionFlexCostType({ paidolId: selectedPaidolId, id, flexCostTypeId: null }));
          dispatch(updateTransactionFlexVendor({ paidolId: selectedPaidolId, id, flexVendorId: null }));
          return;
        }

        // GL changed to a new value — validate existing selections then clear invalids
        const newGlCodeId = option.id;

        (async () => {
          try {
            let isProjectValid = false;
            if (currentTransaction?.flexCodeId) {
              const projectResult = await dispatch(
                searchFlexCodes({
                  paidolId: selectedPaidolId,
                  glCodeId: newGlCodeId,
                  searchTerm: '',
                  specificIds: [currentTransaction.flexCodeId],
                })
              ).unwrap();
              isProjectValid = (projectResult?.items ?? []).some(
                (item: { id: string }) => item.id === currentTransaction.flexCodeId
              );
            }

            const changes: Record<string, null> = {};

            if (!isProjectValid && currentTransaction?.flexCodeId) {
              changes.flexCode = null;
              changes.flexCodeId = null;
              changes.flexCostCode = null;
              changes.flexCostCodeId = null;
              changes.flexCostType = null;
              changes.flexCostTypeId = null;
              changes.flexVendor = null;
              changes.flexVendorId = null;
            } else if (isProjectValid) {
              const validFlexCodeId = currentTransaction?.flexCodeId ?? undefined;
              const [costCodeResult, costTypeResult, vendorResult] = await Promise.all([
                currentTransaction?.flexCostCodeId
                  ? dispatch(
                      searchFlexCostCodes({
                        paidolId: selectedPaidolId,
                        glCodeId: newGlCodeId,
                        flexCodeId: validFlexCodeId,
                        searchTerm: '',
                        specificIds: [currentTransaction.flexCostCodeId],
                      })
                    ).unwrap()
                  : null,
                currentTransaction?.flexCostTypeId
                  ? dispatch(
                      searchFlexCostTypes({
                        paidolId: selectedPaidolId,
                        glCodeId: newGlCodeId,
                        flexCodeId: validFlexCodeId,
                        searchTerm: '',
                        specificIds: [currentTransaction.flexCostTypeId],
                      })
                    ).unwrap()
                  : null,
                currentTransaction?.flexVendorId
                  ? dispatch(
                      searchFlexVendors({
                        paidolId: selectedPaidolId,
                        glCodeId: newGlCodeId,
                        flexCodeId: validFlexCodeId,
                        searchTerm: '',
                        specificIds: [currentTransaction.flexVendorId],
                      })
                    ).unwrap()
                  : null,
              ]);

              if (
                currentTransaction?.flexCostCodeId &&
                !costCodeResult?.items?.some(
                  (item: { id: string }) => item.id === currentTransaction.flexCostCodeId
                )
              ) {
                changes.flexCostCode = null;
                changes.flexCostCodeId = null;
              }
              if (
                currentTransaction?.flexCostTypeId &&
                !costTypeResult?.items?.some(
                  (item: { id: string }) => item.id === currentTransaction.flexCostTypeId
                )
              ) {
                changes.flexCostType = null;
                changes.flexCostTypeId = null;
              }
              if (
                currentTransaction?.flexVendorId &&
                !vendorResult?.items?.some(
                  (item: { id: string }) => item.id === currentTransaction.flexVendorId
                )
              ) {
                changes.flexVendor = null;
                changes.flexVendorId = null;
              }
            }

            if (Object.keys(changes).length > 0) {
              dispatch(applyLocalTransactionUpdates([{ id, changes }]));
              if (changes.flexCodeId === null && currentTransaction?.flexCodeId) {
                dispatch(updateTransactionFlexCode({ paidolId: selectedPaidolId, id, flexCodeId: null }));
              }
              if (changes.flexCostCodeId === null && currentTransaction?.flexCostCodeId) {
                dispatch(
                  updateTransactionFlexCostCode({ paidolId: selectedPaidolId, id, flexCostCodeId: null })
                );
              }
              if (changes.flexCostTypeId === null && currentTransaction?.flexCostTypeId) {
                dispatch(
                  updateTransactionFlexCostType({ paidolId: selectedPaidolId, id, flexCostTypeId: null })
                );
              }
              if (changes.flexVendorId === null && currentTransaction?.flexVendorId) {
                dispatch(updateTransactionFlexVendor({ paidolId: selectedPaidolId, id, flexVendorId: null }));
              }
            }
          } catch (err) {
            captureException(err);
            enqueueSnackbar(t('transactionSidesheet.errorUpdating'), { variant: 'error' });
          }
        })();
      }
    },
    // transaction fields are intentionally read through transactionRef (above)
    // so they are not listed as deps — the handler must always see the latest
    // transaction state, even if a memoized dropdown wrapper holds a stale
    // reference to an earlier callback.
    [dispatch, enqueueSnackbar, selectedPaidolId, id, t, isStructuredMode]
  );

  const handleMarkFlexCodeTransactionAsReviewed = () => {
    if (!transaction || !id) return;
    return dispatch(
      updateFlexCodeTransactionStatus({
        paidolId: selectedPaidolId,
        id,
        flexCodeTransactionStatus: FlexCodeTransactionStatus.REVIEWED,
      })
    );
  };

  const handleApproveFlexcodeTransaction = async () => {
    if (!transaction || !id) return;

    try {
      await dispatch(
        updateFlexCodeTransactionStatus({
          paidolId: selectedPaidolId,
          id,
          flexCodeTransactionStatus: FlexCodeTransactionStatus.APPROVED, // using string directly if not using enum
        })
      ).unwrap();

      // Not submitExpense.* — those belong to reimbursements and read
      // "Expense successfully submitted" on a card-transaction approval.
      enqueueSnackbar(t('transactionSidesheet.approveSuccess', 'Transaction approved'), {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(t('somethingWentWrong', 'Something went wrong. Please try again.'), {
        variant: 'error',
      });
    }
  };

  const isFlexTransactionMissingAllCodes = useMemo(() => {
    return (
      !transaction?.flexCodeId &&
      !transaction?.flexCostCodeId &&
      !transaction?.flexCostTypeId &&
      !transaction?.flexGlCodeId &&
      !transaction?.flexVendorId
    );
  }, [
    transaction?.flexCodeId,
    transaction?.flexCostCodeId,
    transaction?.flexCostTypeId,
    transaction?.flexGlCodeId,
    transaction?.flexVendorId,
  ]);

  return {
    flexCodeTransactionNeedsReview,
    flexCodeTransactionReviewed,
    isFlexReviewed,
    isFlexApproved,
    isTransactionCleared,
    handleUpdateFlexCode,
    handleUpdateFlexGlCode,
    handleUpdateFlexCostCode,
    handleUpdateFlexCostType,
    handleUpdateFlexVendor,
    isFlexTransactionMissingAllCodes,
    handleApproveFlexcodeTransaction,
    handleMarkFlexCodeTransactionAsReviewed,
  };
};
